import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
  Keyboard,
  Modal,
  Dimensions,
} from 'react-native';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { Ionicons } from '@expo/vector-icons';
import { MediaService } from '../services/media.service';
import { useLocale } from '../contexts/LocaleContext';
import { createTranslator } from '../i18n';
import { useTypography } from '../hooks/useTypography';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

const { width: screenWidth } = Dimensions.get('window');

interface MessageInputProps {
  onSendMessage: (content: string, type?: 'text' | 'image' | 'video' | 'audio' | 'document' | 'location', mediaUri?: string, mediaDuration?: number) => void;
  sending?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  sending = false,
  placeholder = 'Type a message...',
  disabled = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const [message, setMessage] = useState('');
  const [inputHeight, setInputHeight] = useState(40);
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const inputRef = useRef<TextInput>(null);
  const { isRTL, locale } = useLocale();
  const needsFlip = isRTL && Platform.OS !== 'web';
  const t = createTranslator(locale);
  const typo = useTypography();
  const rtl = useRTLStyles();

  const handleSend = () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || sending || disabled) return;

    onSendMessage(trimmedMessage, 'text');
    setMessage('');
    setInputHeight(40);
    
    // Keep keyboard open and focus
    inputRef.current?.focus();
  };

  const mediaOptions = [
    {
      id: 'camera',
      title: t('chat.camera'),
      icon: 'camera',
      color: '#4CAF50',
      onPress: () => handleMediaOption('camera')
    },
    {
      id: 'gallery',
      title: t('chat.gallery'),
      icon: 'images',
      color: '#2196F3',
      onPress: () => handleMediaOption('gallery')
    },
    {
      id: 'video',
      title: t('chat.video'),
      icon: 'videocam',
      color: '#FF5722',
      onPress: () => handleMediaOption('video')
    },
    {
      id: 'audio',
      title: t('chat.audio'),
      icon: 'mic',
      color: '#9C27B0',
      onPress: () => handleMediaOption('audio')
    },
    {
      id: 'document',
      title: t('chat.document'),
      icon: 'document-text',
      color: '#FF9800',
      onPress: () => handleMediaOption('document')
    },
    {
      id: 'location',
      title: t('chat.location'),
      icon: 'location',
      color: '#F44336',
      onPress: () => handleMediaOption('location')
    },
  ];

  const handleMediaOption = async (type: string) => {
    setShowMediaOptions(false);
    
    switch (type) {
      case 'camera':
        try {
          const result = await MediaService.takePhoto();
          if (result) {
            onSendMessage(message.trim(), 'image', result.uri);
            setMessage('');
            setInputHeight(40);
          }
        } catch (error) {
          console.error('Error taking photo:', error);
        }
        break;
      case 'gallery':
        try {
          const result = await MediaService.pickImageFromGallery();
          if (result) {
            onSendMessage(message.trim(), 'image', result.uri);
            setMessage('');
            setInputHeight(40);
          }
        } catch (error) {
          console.error('Error picking image:', error);
        }
        break;
      case 'video':
        try {
          const result = await MediaService.pickVideo();
          if (result) {
            onSendMessage(message.trim(), 'video', result.uri);
            setMessage('');
            setInputHeight(40);
          }
        } catch (error) {
          console.error('Error picking video:', error);
        }
        break;
      case 'audio':
        onSendMessage('', 'audio');
        break;
      case 'document':
        try {
          const result = await MediaService.pickDocument();
          if (result) {
            onSendMessage(message.trim(), 'document', result.uri);
            setMessage('');
            setInputHeight(40);
          }
        } catch (error) {
          console.error('Error picking document:', error);
        }
        break;
      case 'location':
        onSendMessage(message.trim(), 'location');
        setMessage('');
        setInputHeight(40);
        break;
    }
  };

  const handleAttachment = () => {
    setShowMediaOptions(true);
  };

  const handleContentSizeChange = (event: any) => {
    const { height } = event.nativeEvent.contentSize;
    const maxHeight = 120; // Maximum height for input
    const minHeight = 40;  // Minimum height for input
    
    setInputHeight(Math.min(Math.max(height, minHeight), maxHeight));
  };

  const isDisabled = disabled || sending;
  const canSend = message.trim().length > 0 && !isDisabled;

  return (
    <View style={styles.container}>
      <View style={[styles.inputContainer, needsFlip && { flexDirection: 'row-reverse' }]}>
        {/* Attachment Button */}
        <TouchableOpacity
          style={[styles.attachmentButton, isDisabled && styles.disabledButton, isRTL && { marginRight: 0, marginLeft: spacing.sm }]}
          onPress={handleAttachment}
          disabled={isDisabled}
          activeOpacity={0.7}
        >
          <Ionicons 
            name="add" 
            size={24} 
            color={isDisabled ? colors.textLight : colors.primary} 
          />
        </TouchableOpacity>

        {/* Text Input */}
        <TextInput
          ref={inputRef}
          style={[
            styles.textInput,
            { height: Math.max(inputHeight, 40) },
            isDisabled && styles.disabledInput,
            rtl.marginStart(spacing.sm),
            { textAlign: rtl.textStart }
          ]}
          value={message}
          onChangeText={setMessage}
          placeholder={placeholder || t('chat.typeMessage')}
          placeholderTextColor={colors.text.secondary}
          multiline
          maxLength={4096} // WhatsApp message limit
          onContentSizeChange={handleContentSizeChange}
          textAlignVertical="center"
          editable={!isDisabled}
          blurOnSubmit={false}
          onSubmitEditing={handleSend}
          returnKeyType="send"
          enablesReturnKeyAutomatically
        />

        {/* Send Button */}
        <TouchableOpacity
          style={[
            styles.sendButton,
            canSend ? styles.sendButtonActive : styles.sendButtonInactive,
            isDisabled && styles.disabledButton
          ]}
          onPress={handleSend}
          disabled={!canSend}
          activeOpacity={0.8}
        >
          {sending ? (
            <Ionicons name="hourglass" size={20} color="white" />
          ) : (
            <Ionicons
              name={rtl.mirrorIcon('send') as any}
              size={20}
              color={canSend ? "white" : colors.textLight}
            />
          )}
        </TouchableOpacity>
      </View>

      {/* Character Counter (shown when approaching limit) */}
      {message.length > 3500 && (
        <View style={styles.characterCounter}>
          <Text style={[
            styles.characterCountText,
            message.length > 4000 && styles.characterCountWarning
          ]}>
            {message.length}/4096
          </Text>
        </View>
      )}
      {/* Media Options Modal */}
      <Modal
        visible={showMediaOptions}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowMediaOptions(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowMediaOptions(false)}
        >
          <View style={styles.mediaOptionsContainer}>
            <View style={styles.mediaOptionsHeader}>
              <Text style={styles.mediaOptionsTitle}>{t('chat.sendMedia')}</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => setShowMediaOptions(false)}
              >
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            
            <View style={styles.mediaOptionsGrid}>
              {mediaOptions.map((option) => (
                <TouchableOpacity
                  key={option.id}
                  style={styles.mediaOptionItem}
                  onPress={option.onPress}
                  activeOpacity={0.7}
                >
                  <View style={[styles.mediaOptionIcon, { backgroundColor: option.color }]}>
                    <Ionicons name={option.icon as any} size={24} color="white" />
                  </View>
                  <Text style={styles.mediaOptionText}>{option.title}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    backgroundColor: colors.background.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingBottom: Platform.OS === 'ios' ? 0 : spacing.sm,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    minHeight: 56,
  },
  attachmentButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.background.secondary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
    marginBottom: 2,
  },
  attachmentIcon: {
    fontSize: 18,
    color: colors.text.primary,
  },
  textInput: {
    flex: 1,
    backgroundColor: colors.background.secondary,
    borderRadius: 20,
    paddingHorizontal: spacing.md,
    paddingVertical: Platform.OS === 'ios' ? spacing.sm : spacing.xs,
    fontSize: 16,
    color: colors.text.primary,
    maxHeight: 120,
    minHeight: 40,
    textAlignVertical: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    marginRight: spacing.sm,
    fontFamily: typography.fontFamily.regular,
  },
  sendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 2,
  },
  sendButtonActive: {
    backgroundColor: colors.primary,
  },
  sendButtonInactive: {
    backgroundColor: colors.background.secondary,
  },
  sendIcon: {
    fontSize: 18,
    fontWeight: '600',
  },
  sendIconActive: {
    color: colors.white,
  },
  sendIconInactive: {
    color: colors.text.secondary,
  },
  characterCounter: {
    alignItems: 'flex-end',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xs,
  },
  characterCountText: {
    fontSize: 12,
    color: colors.text.secondary,
  },
  characterCountWarning: {
    color: colors.error,
    fontWeight: '600',
  },
  disabledButton: {
    opacity: 0.5,
  },
  disabledInput: {
    opacity: 0.5,
    backgroundColor: colors.background.disabled,
  },
  disabledText: {
    color: colors.textLight,
  },

  // Media Options Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },

  mediaOptionsContainer: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.xl,
    borderTopRightRadius: designTokens.borderRadius.xl,
    paddingBottom: Platform.OS === 'ios' ? 34 : designTokens.spacing.lg,
    ...designTokens.shadows.xl,
  },

  mediaOptionsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: designTokens.spacing.lg,
    paddingVertical: designTokens.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: withAlpha(colors.border, 0.13),
  },

  mediaOptionsTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },

  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: withAlpha(colors.lightGray, 0.25),
  },

  mediaOptionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: designTokens.spacing.lg,
    paddingTop: designTokens.spacing.lg,
    justifyContent: 'space-between',
  },

  mediaOptionItem: {
    width: (screenWidth - designTokens.spacing.lg * 2 - designTokens.spacing.md * 2) / 3,
    alignItems: 'center',
    marginBottom: designTokens.spacing.lg,
  },

  mediaOptionIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: designTokens.spacing.sm,
    ...designTokens.shadows.md,
  },

  mediaOptionText: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    textAlign: 'center',
  },
});

